/**
 * The Dispatch view, the state a person lays over it, and the page's own settings.
 *
 * A view is what a row is (the grain), how the rows are narrowed and ordered (the query), and how they are drawn (the
 * layout).
 *
 * Views live in the workspace config cascade under `web.dispatch.views`, keyed by view id. The state a member (or the
 * workspace, as everyone's defaults) lays over each lives beside them under `web.dispatch.viewState`. Both layer key
 * by key like the rest of the config, with no rule of their own: every value in these documents is a scalar, an
 * array, or a record meant to layer, so the cascade's one dumb merge is enough (see {@link DispatchField}). The page's
 * own behavior (the keymap and the shortcut bar) belongs to the page rather than to any view and sits directly under
 * `web.dispatch` ({@link DispatchPageSettings}). Every field reference is the predicate DSL's key for a
 * {@link FieldRef} — a tag column is `tag:<slug>`, never `tag:<id>` — so a view written for one workspace survives
 * being copied to another.
 * {@link DispatchConfigSchema} is the whole block as the workspace wire validates it, {@link CompiledDispatchConfig}
 * the block the cascade yields, and {@link ResolvedDispatchConfig} the block a page reads. The cascade stores only
 * what someone wrote: the built-in {@link DEFAULT_DISPATCH_VIEW} lives here in code and is laid beneath whatever the
 * cascade yields by {@link withBuiltInDispatchViews}. Browser-safe: no React, no DOM.
 */

import { z } from "zod";
import { deepFreeze } from "@storyboard/config-cascade/cascade";
import type { DispatchColor, DispatchCoreKind, DispatchEntity } from "./fieldKinds";
import { DISPATCH_COLORS, DISPATCH_CORE_KINDS, isDispatchCoreKind } from "./fieldKinds";
import type { FieldRef, FilterNode } from "@storyboard/driver-query/predicate";
import { filterNodeSchema, parseFieldKey } from "@storyboard/driver-query/predicate";
import type { RequiresAcknowledgement } from "./requiresAcknowledgement";
import { requiresAcknowledgementSchema } from "./requiresAcknowledgement";

// The kinds and colors are the registry's (`fieldKinds.ts`); re-exported here because the view schema is where most
// callers first meet them.
export { DISPATCH_COLORS, DISPATCH_CORE_KINDS };
export type { DispatchColor, DispatchCoreKind };

/**
 * What one row of a view is about: a driver (the latest activity summarized onto them) or one message.
 *
 * The entity vocabulary is the registry's.
 */
export type DispatchGrain = DispatchEntity;

/**
 * A field as a Dispatch document names it: the predicate DSL's key for a {@link FieldRef}.
 *
 * `core:<kind>` or `tag:<slug>`, a string rather than the ref object on purpose. These documents layer key by key
 * through the config cascade, and a
 * `core` ref laid over a `tag` ref would compose an object no schema admits; a string replaces. {@link fieldRefOf}
 * recovers the ref where a predicate leaf needs one.
 */
export type DispatchField = string;

/**
 * The {@link FieldRef} a Dispatch field key names.
 *
 * Only for a key the schema has admitted: a malformed key is a programming error here, not a document to tolerate.
 */
export function fieldRefOf(field: DispatchField): FieldRef {
  const ref = parseFieldKey(field);
  if (ref === null) {
    throw new Error(`"${field}" is not a field key`);
  }
  return ref;
}

/**
 * One column of the Dispatch table.
 */
export type DispatchColumn = {
  field: DispatchField;
  /** Display override; defaults from the field. */
  label?: string;
  width?: number;
  /** Pinned columns stay fixed on the left while the rest scroll. */
  pinned?: boolean;
  /** Paints every value in the column; `valueColors` wins for the values it names. */
  color?: DispatchColor;
  /** Paints particular values (by exact projected value) so, say, one duty status stands out. */
  valueColors?: Record<string, DispatchColor>;
};

/**
 * One control in the Dispatch top bar; `default` is its value when nothing in the state cascade says otherwise.
 *
 * `id` is the control's kebab-case key in view state, so two controls may target one field. A `toggle` carries either
 * a `field` (keep rows whose field is truthy: a true boolean, non-blank text, a tag with any value, so a toggle over a
 * "vehicle" tag means "has a vehicle assigned") or a `predicate` (any filter tree, the "specific sub-query" switch),
 * never both. A `values` control picks from the values the rows carry and renders by the field's kind. A `time`
 * control keeps rows whose timestamp is within the chosen number of minutes.
 */
export type DispatchControl =
  | { kind: "toggle"; id: string; label: string; field?: DispatchField; predicate?: FilterNode; default?: boolean }
  | { kind: "values"; id: string; field: DispatchField; label?: string; multiple?: boolean; default?: string[] }
  | { kind: "time"; id: string; field: DispatchField; label?: string; presetMinutes?: number[]; default?: number };

/**
 * The value a control holds in view state: on/off, the chosen values, or a number of minutes.
 */
export type DispatchControlValue = boolean | string[] | number;

/**
 * The order rows come in: by one field, ascending or descending.
 */
export type DispatchSort = { field: DispatchField; direction: "asc" | "desc" };

/**
 * How a set of records is narrowed and ordered, apart from how it is drawn.
 *
 * The reusable half of a view: a count badge, a notification rule, a map, or a fleet-message audience needs a query
 * and no layout.
 */
export type DispatchQuery = {
  /** Ordered controls in the top bar, rendered left to right. */
  controls: DispatchControl[];
  /** Free-text search scans these fields. */
  search: DispatchField[];
  /** Absent or `"none"` leaves rows in the order the data arrives. */
  sort?: DispatchSort | DispatchNone;
  /** Rows are grouped under this field's values when set; view state may override or clear it. */
  groupBy?: DispatchField | DispatchNone;
};

/**
 * A table: the columns, ordered left to right. The one layout today; a board or card layout is additive.
 */
export type DispatchTableLayout = { kind: "table"; columns: DispatchColumn[] };

/**
 * How a view draws its rows.
 */
export type DispatchLayout = DispatchTableLayout;

/**
 * A complete Dispatch view: grain, query, layout.
 */
export type DispatchViewDefinition = {
  /** Kebab-case natural key, unique across the config cascade. */
  id: string;
  label: string;
  /** What a row is; a driver when absent. */
  grain?: DispatchGrain;
  query: DispatchQuery;
  layout: DispatchLayout;
  /** What the view's team is asked to acknowledge; driver messages alone when absent. */
  requiresAcknowledgement?: RequiresAcknowledgement;
};

/**
 * The token a `sort` or `groupBy` carries to mean none where the layer beneath has one: unsorted, ungrouped.
 *
 * `null` cannot mean none, because everywhere in the config cascade `null` means "inherit from the layer beneath", and
 * leaving the key out cannot either, since the cascade lays a document key by key and an omitted key inherits: a
 * view stored over one with a sort needs the token to be unsorted. Conditions need no token: an empty `adhoc` list
 * narrows nothing.
 */
export const NONE = "none";
export type DispatchNone = typeof NONE;

/**
 * The parts of a query a person may lay over the layer beneath.
 *
 * Inside a state document an absent or `null` key inherits from the layer beneath, ultimately the view; `"none"` on
 * `sort` or `groupBy` and an empty `adhoc` mean none where the layer beneath has something.
 */
export type DispatchQueryState = {
  /** Control values by control id, over the controls' defaults; a `null` value inherits that control's. */
  controls?: Record<string, DispatchControlValue | null> | null;
  /** Conditions the person added on top of the controls, all of which a row must meet. */
  adhoc?: FilterNode[] | null;
  sort?: DispatchSort | DispatchNone | null;
  groupBy?: DispatchField | DispatchNone | null;
  /** Group keys folded shut, when grouped. */
  collapsedGroups?: string[] | null;
};

/**
 * The parts of a table layout a person may lay over the view's, in the shape the table keeps them by column id.
 */
export type DispatchTableLayoutState = {
  columns?: {
    visibility?: Record<string, boolean | null> | null;
    order?: string[] | null;
    sizing?: Record<string, number | null> | null;
    pinning?: { left: string[] } | null;
  } | null;
};

/**
 * What a layer lays over a view: the definition is the template, this is the state laid over it.
 *
 * It takes the same shape as the parts of the definition it overlays.
 *
 * Lives under `web.dispatch.viewState[viewId]` at both cascade levels. A workspace entry is everyone's defaults and a
 * member's layers over it key by key, the way every other config value cascades: an absent or `null` key inherits from
 * the layer beneath, ultimately the view, a record layers, and an array or scalar replaces. The same shape is a sparse
 * patch: the web sends the keys that changed and `null` for an override it takes back.
 * {@link resolveDispatchView} lays the compiled entry over the view.
 */
export type DispatchViewState = {
  /** The row grain, over the view's. */
  grain?: DispatchGrain | null;
  query?: DispatchQueryState | null;
  layout?: DispatchTableLayoutState | null;
  /** What this person is asked to acknowledge, over the view's list; the whole list replaces the view's. */
  requiresAcknowledgement?: RequiresAcknowledgement | null;
};

/**
 * The keyboard-driven actions the page's `keymap` may rebind.
 *
 * `acknowledge` acknowledges what the row is about at the view's grain (a driver row's channel, a message row's one
 * message); `acknowledgeChannel` always acknowledges the driver's whole channel; `unacknowledge` takes the row's
 * acknowledgement back at the same grain; `toggleSelect` ticks the selected row for a bulk action; `fleetMessage`
 * opens the composer addressed to the rows shown or ticked; `task` opens the task picker on the highlighted or ticked
 * rows, offered only while the workspace's task surface is on; `nextPage` and `prevPage` move the highlight a
 * screenful, or page the open popover tab (History or Context); `searchDriver` opens the workspace's global search,
 * and `focusSearch` puts the cursor in the page's own search box, as the classic driver list's search key does. Shift
 * with the arrows extends the ticked range, Ctrl or Cmd with A ticks every row, and Ctrl or Cmd with Z undoes the last
 * acknowledgement, the way a spreadsheet does; none of those three is rebindable.
 *
 * The order is collision precedence (`resolveDispatchPageSettings`) and key-matching order, not the order the
 * shortcut bar lists them in.
 */
export const DISPATCH_ACTIONS = [
  "nextRow",
  "prevRow",
  "nextPage",
  "prevPage",
  "reply",
  "send",
  "acknowledge",
  "acknowledgeChannel",
  "unacknowledge",
  "toggleSelect",
  "openHistory",
  "openContext",
  "task",
  "fleetMessage",
  "searchDriver",
  "focusSearch",
  "closePanel",
  "showShortcuts",
] as const;
export type DispatchAction = (typeof DISPATCH_ACTIONS)[number];

/**
 * The order the shortcut helper lists the actions in, the same for every workspace.
 *
 * Most used first: what a dispatcher does to the driver in front of them, then moving between drivers, then the rest.
 * It holds every action but `showShortcuts`, which the helper always lists last. Apart from {@link DISPATCH_ACTIONS}
 * on purpose: that order decides which action keeps a contested key, and listing must never change that.
 */
export const DISPATCH_SHORTCUT_ORDER: readonly DispatchAction[] = [
  "openContext",
  "openHistory",
  "reply",
  "acknowledge",
  "task",
  "focusSearch",
  "fleetMessage",
  "nextRow",
  "prevRow",
  "nextPage",
  "prevPage",
  "send",
  "acknowledgeChannel",
  "unacknowledge",
  "toggleSelect",
  "searchDriver",
  "closePanel",
];

/**
 * Overrides of {@link DEFAULT_DISPATCH_KEYMAP}, per action: the compiled keymap the page reads.
 */
export type DispatchKeymap = Partial<Record<DispatchAction, string>>;

/**
 * One layer's stored keymap: a binding per action it overrides, or `null` for one it hands back to the layer beneath.
 *
 * The `null` is the cascade's inherit. The compiled {@link DispatchKeymap} carries no `null`; the cascade resolves
 * them.
 */
export type DispatchKeymapLayer = Partial<Record<DispatchAction, string | null>>;

/**
 * The page's own behavior, apart from any view.
 *
 * What the keys do, whether the shortcut bar shows, and which tag the Call button reads a phone number from.
 *
 * Sits directly under `web.dispatch` and cascades workspace to member, the keymap merging binding by binding like any
 * record and `null` handing a layer's bindings back to the layer beneath. {@link DispatchKeymapSchema} checks one
 * layer's bindings for collisions over the defaults; two layers that collide once merged are settled by
 * `resolveDispatchPageSettings`.
 */
export type DispatchPageSettings = {
  keymap?: DispatchKeymap | null;
  /** Whether the shortcut bar shows under the header; shown when absent. */
  showShortcuts?: boolean;
  /** The slug of the tag holding drivers' phone numbers for the Call button; no tag is read when absent. */
  phoneTagSlug?: string;
};

/**
 * The bindings in force when no keymap says otherwise.
 *
 * Mostly a letter named for what it does: `a` acknowledges and Shift+A takes it back, `c` and `h` open Context and
 * History, `t` makes a task, and `s` searches the page while Shift+S opens the global search. `ArrowDown`/`ArrowUp`
 * (and J/K) always also move selection.
 */
export const DEFAULT_DISPATCH_KEYMAP: Record<DispatchAction, string> = {
  nextRow: "Tab",
  prevRow: "Shift+Tab",
  nextPage: "PageDown",
  prevPage: "PageUp",
  reply: "r",
  send: "Enter",
  acknowledge: "a",
  acknowledgeChannel: "Shift+m",
  unacknowledge: "Shift+a",
  toggleSelect: "Space",
  openHistory: "h",
  openContext: "c",
  task: "t",
  fleetMessage: "f",
  searchDriver: "Shift+s",
  focusSearch: "s",
  closePanel: "Escape",
  showShortcuts: "?",
};

/**
 * What each action is called, wherever it is listed.
 *
 * One wording for the shortcut bar and Internal Ops' keymap editor, so an operator binding a key reads the name a
 * dispatcher sees.
 */
export const DISPATCH_ACTION_LABELS: Record<DispatchAction, string> = {
  nextRow: "Next driver",
  prevRow: "Previous driver",
  nextPage: "Next page",
  prevPage: "Previous page",
  reply: "Reply",
  send: "Send",
  acknowledge: "Acknowledge",
  acknowledgeChannel: "Acknowledge channel",
  unacknowledge: "Unacknowledge",
  toggleSelect: "Tick row",
  openHistory: "History",
  openContext: "Context",
  task: "Make a task…",
  fleetMessage: "Fleet message",
  searchDriver: "Global search",
  focusSearch: "Search for a driver",
  closePanel: "Close / clear selection",
  showShortcuts: "All shortcuts",
};

/**
 * Canonical form of a key binding for comparison.
 *
 * Modifiers and key are lowercased and whitespace dropped, so `Shift+Tab` and `shift + tab` bind the same key.
 */
export function normalizeKeyBinding(binding: string): string {
  return binding.replace(/\s+/g, "").toLowerCase();
}

const KEBAB_CASE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** A {@link DispatchField} a view may use: a `tag:` or `core:` key, and the core kind must be known. */
const dispatchFieldSchema: z.ZodType<DispatchField> = z.string().superRefine((field, ctx) => {
  const ref = parseFieldKey(field);
  if (ref === null) {
    ctx.addIssue({ code: "custom", message: `"${field}" is not a field key (core:<kind> or tag:<slug>)` });
    return;
  }
  if (ref.strategy === "eld") {
    ctx.addIssue({ code: "custom", message: "Dispatch views cannot reference eld fields" });
    return;
  }
  if (ref.strategy === "core" && !isDispatchCoreKind(ref.kind)) {
    ctx.addIssue({ code: "custom", message: `Unknown Dispatch core kind "${ref.kind}"` });
  }
});

const dispatchColorSchema = z.enum(DISPATCH_COLORS);

const controlId = z.string().regex(KEBAB_CASE, "Control id must be kebab-case");

/** The predicate schema typed to its node shape, so a control's `predicate` infers as a `FilterNode`. */
const predicateSchema = filterNodeSchema as z.ZodType<FilterNode>;

const dispatchGrainSchema = z.enum(["driver", "message"]);

const noneSchema = z.literal(NONE);

/** A sort as the state's `sort` reads it; the state schema sets the unknown-key policy above it. */
const sortSchema = z.object({ field: dispatchFieldSchema, direction: z.enum(["asc", "desc"]) });

/**
 * Builds the view definition schema with one unknown-key policy at every depth.
 *
 * The same shapes serve two readers: the wire and internal tooling refuse a key the format does not know, since a
 * misspelled key silently stripped is a save that read as success; the web strips it, since a view a later release
 * extended must still render on a client that predates the key.
 *
 * @param object - `z.strictObject` or `z.object`, the policy applied to every object in the document.
 */
function viewDefinitionSchema(object: typeof z.strictObject): z.ZodType<DispatchViewDefinition> {
  const column = object({
    field: dispatchFieldSchema,
    label: z.string().min(1).optional(),
    width: z.number().positive().optional(),
    pinned: z.boolean().optional(),
    color: dispatchColorSchema.optional(),
    valueColors: z.record(z.string(), dispatchColorSchema).optional(),
  });

  const control = z
    .discriminatedUnion("kind", [
      object({
        kind: z.literal("toggle"),
        id: controlId,
        label: z.string().min(1),
        field: dispatchFieldSchema.optional(),
        predicate: predicateSchema.optional(),
        default: z.boolean().optional(),
      }),
      object({
        kind: z.literal("values"),
        id: controlId,
        field: dispatchFieldSchema,
        label: z.string().min(1).optional(),
        multiple: z.boolean().optional(),
        default: z.array(z.string()).optional(),
      }),
      object({
        kind: z.literal("time"),
        id: controlId,
        field: dispatchFieldSchema,
        label: z.string().min(1).optional(),
        presetMinutes: z.array(z.number().positive()).optional(),
        default: z.number().positive().optional(),
      }),
    ])
    .superRefine((value, ctx) => {
      if (value.kind === "toggle" && (value.field === undefined) === (value.predicate === undefined)) {
        ctx.addIssue({ code: "custom", message: `Toggle "${value.id}" needs exactly one of field or predicate` });
      }
    });

  const query = object({
    controls: z.array(control),
    search: z.array(dispatchFieldSchema),
    sort: z.union([object({ field: dispatchFieldSchema, direction: z.enum(["asc", "desc"]) }), noneSchema]).optional(),
    groupBy: z.union([dispatchFieldSchema, noneSchema]).optional(),
  }).superRefine((value, ctx) => {
    const controlIds = new Set<string>();
    for (const item of value.controls) {
      if (controlIds.has(item.id)) {
        ctx.addIssue({ code: "custom", message: `Control "${item.id}" appears more than once`, path: ["controls"] });
      }
      controlIds.add(item.id);
    }
  });

  const tableLayout = object({
    kind: z.literal("table"),
    columns: z.array(column).min(1),
  }).superRefine((value, ctx) => {
    const seen = new Set<string>();
    for (const item of value.columns) {
      if (seen.has(item.field)) {
        ctx.addIssue({ code: "custom", message: `Column "${item.field}" appears more than once`, path: ["columns"] });
      }
      seen.add(item.field);
    }
  });

  return object({
    id: z.string().regex(KEBAB_CASE, "View id must be kebab-case"),
    label: z.string().min(1),
    grain: dispatchGrainSchema.optional(),
    query,
    layout: z.discriminatedUnion("kind", [tableLayout]),
    requiresAcknowledgement: requiresAcknowledgementSchema(object).optional(),
  }) as z.ZodType<DispatchViewDefinition>;
}

// An object of optional keys rather than `z.partialRecord`: apps that bundle this file resolve `zod` to their own
// version, and the Zod 3 ones have no `partialRecord`. Checked over the keymap laid on the defaults, not the overrides
// alone: `{ reply: "a" }` is one entry with no duplicate, yet in force it binds `a` to both `reply` and the default
// `acknowledge` and silently orphans one of them.
const dispatchKeymapShape = Object.fromEntries(
  DISPATCH_ACTIONS.map((action) => [action, z.string().min(1).nullable().optional()]),
) as Record<DispatchAction, z.ZodOptional<z.ZodNullable<z.ZodString>>>;

/**
 * Validates one layer's keymap: known actions only, and no key bound to two actions once laid over the defaults.
 *
 * A `null` binding is the cascade's inherit and binds nothing here. A key for an action that does not exist is a
 * typo, not a newer release's field, so unknown keys are refused. The strict schema, for the workspace wire and
 * internal tooling; the web reads with {@link lenientDispatchKeymapSchema}.
 */
export const DispatchKeymapSchema: z.ZodType<DispatchKeymapLayer> = z
  .object(dispatchKeymapShape)
  .strict()
  .superRefine((keymap, ctx) => {
    const seen = new Map<string, string>();
    for (const [action, binding] of Object.entries({ ...DEFAULT_DISPATCH_KEYMAP, ...keymap })) {
      if (binding == null) {
        continue;
      }
      const normalized = normalizeKeyBinding(binding);
      const other = seen.get(normalized);
      if (other !== undefined) {
        ctx.addIssue({ code: "custom", message: `"${binding}" is bound to both ${other} and ${action}` });
      }
      seen.set(normalized, action);
    }
  })
  .meta({ id: "DispatchKeymap" });

/**
 * Reads a keymap keeping every binding that parses and dropping the rest.
 *
 * Dropped: an unknown action, an empty or `null` binding. Never fails: a value that is not an object reads as no
 * bindings, and the web renders what it can of a stored keymap rather than showing an error or falling back to the
 * defaults whole. A collision between two bindings is left for `resolveDispatchPageSettings` to settle.
 */
export const lenientDispatchKeymapSchema: z.ZodType<DispatchKeymap> = z
  .object(Object.fromEntries(DISPATCH_ACTIONS.map((action) => [action, z.string().min(1).optional().catch(undefined)])))
  .catch({}) as z.ZodType<DispatchKeymap>;

/**
 * Validates a {@link DispatchViewDefinition} beyond its shape.
 *
 * Tag and core keys only, known core kinds, one column per field, one control per id, and no key the format does
 * not know at any depth. The strict schema, for the workspace wire and internal tooling; the web reads with
 * {@link lenientDispatchViewDefinitionSchema}.
 */
export const DispatchViewDefinitionSchema: z.ZodType<DispatchViewDefinition> = viewDefinitionSchema(
  z.strictObject,
).meta({
  id: "DispatchViewDefinition",
});

/**
 * Reads a {@link DispatchViewDefinition} stripping any key the format does not know, at any depth.
 *
 * The same checks as {@link DispatchViewDefinitionSchema} otherwise: half a view cannot render, so a view that fails
 * is dropped whole. What the web renders from, so a field a later release adds never costs a client the view.
 */
export const lenientDispatchViewDefinitionSchema: z.ZodType<DispatchViewDefinition> = viewDefinitionSchema(z.object);

const controlValueSchema = z.union([z.boolean(), z.array(z.string()), z.number()]);
const stateSortSchema = z.union([sortSchema, noneSchema]);
const stateGroupBySchema = z.union([dispatchFieldSchema, noneSchema]);
const pinningSchema = z.object({ left: z.array(z.string()) }).strict();

/**
 * Validates a {@link DispatchViewState} as one layer stores it: every key optional or `null`, no unknown keys.
 *
 * The strict schema, for the workspace wire and internal tooling; the web reads with
 * {@link lenientDispatchViewStateSchema}.
 */
export const DispatchViewStateSchema: z.ZodType<DispatchViewState> = z
  .object({
    grain: dispatchGrainSchema.nullable().optional(),
    query: z
      .object({
        controls: z.record(z.string(), controlValueSchema.nullable()).nullable().optional(),
        adhoc: z.array(predicateSchema).nullable().optional(),
        sort: stateSortSchema.nullable().optional(),
        groupBy: stateGroupBySchema.nullable().optional(),
        collapsedGroups: z.array(z.string()).nullable().optional(),
      })
      .strict()
      .nullable()
      .optional(),
    layout: z
      .object({
        columns: z
          .object({
            visibility: z.record(z.string(), z.boolean().nullable()).nullable().optional(),
            order: z.array(z.string()).nullable().optional(),
            sizing: z.record(z.string(), z.number().nullable()).nullable().optional(),
            pinning: pinningSchema.nullable().optional(),
          })
          .strict()
          .nullable()
          .optional(),
      })
      .strict()
      .nullable()
      .optional(),
    requiresAcknowledgement: requiresAcknowledgementSchema(z.strictObject).nullable().optional(),
  })
  .strict()
  .meta({ id: "DispatchViewState" }) as z.ZodType<DispatchViewState>;

/**
 * A key that reads as absent, and so inherits, when its value fails to parse.
 */
function forgiving<T extends z.ZodType>(schema: T) {
  return schema.optional().catch(undefined);
}

/**
 * Reads a {@link DispatchViewState} keeping every key that parses and treating the rest as absent.
 *
 * Never fails: one bad `sort` costs the member that key, not their column sizing beside it, a key a later release
 * added is ignored rather than refused, and a value that is not an object reads as an entry that sets nothing. What
 * the web renders from.
 */
export const lenientDispatchViewStateSchema: z.ZodType<DispatchViewState> = z
  .object({
    grain: forgiving(dispatchGrainSchema.nullable()),
    query: forgiving(
      z
        .object({
          controls: forgiving(z.record(z.string(), controlValueSchema.nullable()).nullable()),
          adhoc: forgiving(z.array(predicateSchema).nullable()),
          sort: forgiving(stateSortSchema.nullable()),
          groupBy: forgiving(stateGroupBySchema.nullable()),
          collapsedGroups: forgiving(z.array(z.string()).nullable()),
        })
        .nullable(),
    ),
    layout: forgiving(
      z
        .object({
          columns: forgiving(
            z
              .object({
                visibility: forgiving(z.record(z.string(), z.boolean().nullable()).nullable()),
                order: forgiving(z.array(z.string()).nullable()),
                sizing: forgiving(z.record(z.string(), z.number().nullable()).nullable()),
                pinning: forgiving(pinningSchema.nullable()),
              })
              .nullable(),
          ),
        })
        .nullable(),
    ),
    requiresAcknowledgement: forgiving(requiresAcknowledgementSchema(z.object).nullable()),
  })
  .catch({}) as z.ZodType<DispatchViewState>;

/**
 * Refuses a `views` entry stored under a key other than its own id, so the map key and the document never disagree.
 */
function viewKeysMatchIds(views: Record<string, DispatchViewDefinition | null>, ctx: z.RefinementCtx): void {
  for (const [key, view] of Object.entries(views)) {
    if (view !== null && view.id !== key) {
      ctx.addIssue({ code: "custom", message: `View "${view.id}" is stored under key "${key}"`, path: [key, "id"] });
    }
  }
}

/**
 * The whole `web.dispatch` block as the workspace wire validates it: strict, every key optional, documents exact.
 *
 * A `null` view or state entry and a `null` keymap are resets that pass. What the admin API and internal ops refuse a
 * workspace document by; the membership wire reads with {@link lenientMembershipDispatchConfigSchema}. The workspace
 * config embeds this instance as `web.dispatch`, and it is the component `DispatchWebConfig` on the wire, its documents
 * the components they name themselves.
 */
export const DispatchConfigSchema = z
  .strictObject({
    enabled: z.boolean().optional(),
    defaultViewId: z.string().optional(),
    keymap: DispatchKeymapSchema.nullable().optional(),
    showShortcuts: z.boolean().optional(),
    /**
     * The slug of the workspace tag whose first value is a driver's phone number, for the popover's Call button.
     *
     * Leaving it unset means no tag is read, so the button dials only an account's or an invitation's number.
     */
    phoneTagSlug: z.string().min(1).optional(),
    views: z.record(z.string(), DispatchViewDefinitionSchema.nullable()).superRefine(viewKeysMatchIds).optional(),
    viewState: z.record(z.string(), DispatchViewStateSchema.nullable()).optional(),
    /** Turns the task surface on: the picker, the shelf, the chip, and the task sync. Workspace-only. */
    tasks: z.boolean().optional(),
    /** Lets a new task be about a message or event; read only while `tasks` is on. Workspace-only. */
    itemTasks: z.boolean().optional(),
  })
  .meta({ id: "DispatchWebConfig" });

/**
 * One layer's `web.dispatch` block, which is also the sparse patch a client sends.
 *
 * Every key optional, `null` handing a key back to the layer beneath.
 */
export type DispatchConfig = z.infer<typeof DispatchConfigSchema>;

/** {@link DispatchConfigSchema} without the workspace-only keys: what internal ops holds a member's document to. */
export const MembershipDispatchConfigSchema = DispatchConfigSchema.omit({
  enabled: true,
  phoneTagSlug: true,
  tasks: true,
  itemTasks: true,
});

/**
 * The `web.dispatch` keys only the workspace may set.
 *
 * A member cannot turn the page, its tasks, or tasks about a message or event on or off for themselves, or choose which
 * tag holds drivers' phone numbers.
 *
 * The membership wire passes unknown keys through, so the platform scrubs these from a membership write rather than
 * refusing it.
 */
export const DISPATCH_WORKSPACE_ONLY_KEYS: ReadonlySet<string> = new Set([
  "enabled",
  "phoneTagSlug",
  "tasks",
  "itemTasks",
]);

/**
 * The block as the member-facing membership PATCH accepts it: lenient by design.
 *
 * The web client writes this layer, and a dispatcher must never see an error or lose a personalization to one, so the
 * documents are opaque and unknown keys pass through: a newer web against an older server stores what it sent, and
 * every reader validates what it renders (the web through the lenient document schemas above). The one thing still
 * refused is a wrongly typed scalar, which is a client bug. `enabled`, `phoneTagSlug`, `tasks`, and `itemTasks` are not
 * declared: they are workspace-only, and the platform's membership scrub drops them should a client send them anyway.
 * The membership config embeds this instance, the component `MembershipDispatchWebConfig` on the wire.
 */
export const lenientMembershipDispatchConfigSchema = z
  .looseObject({
    defaultViewId: z.string().optional(),
    /** Bindings by action name; `null` hands the member's bindings back to the workspace's. */
    keymap: z.record(z.string(), z.unknown()).nullable().optional(),
    showShortcuts: z.boolean().optional(),
    /** Whole `DispatchViewDefinition` documents by id, or `null` to inherit; validated by the reader. */
    views: z.record(z.string(), z.unknown()).optional(),
    /** `DispatchViewState` entries by view id, or `null` to inherit; validated by the reader. */
    viewState: z.record(z.string(), z.unknown()).optional(),
  })
  .meta({ id: "MembershipDispatchWebConfig" });

/**
 * The `web.dispatch` block the config cascade yields: every setting decided, the documents as stored.
 *
 * A views or state entry is `null` where a layer reset one that nothing lay beneath, and is otherwise the document as
 * the winning layer stored it, unvalidated: the cascade passes documents through and the reader validates. The code
 * defaults seed no view; the built-in default is laid beneath by {@link withBuiltInDispatchViews} on the way to a
 * {@link ResolvedDispatchConfig}.
 */
export type CompiledDispatchConfig = {
  enabled: boolean;
  defaultViewId: string;
  /** The bindings the layers set over the page's defaults; a stored `null` inherits and never reaches here. */
  keymap: DispatchKeymap;
  showShortcuts: boolean;
  /** Absent unless the workspace set it; the code defaults carry none. */
  phoneTagSlug?: string;
  /** Whether the task surface is on; workspace-only. */
  tasks: boolean;
  /** Whether a new task may be about a message or event; workspace-only, and read only while `tasks` is on. */
  itemTasks: boolean;
  views: Record<string, DispatchViewDefinition | null>;
  viewState: Record<string, DispatchViewState | null>;
};

/**
 * The block as a page reads it.
 *
 * The compiled block with the built-in view beneath, the resets resolved away, and only the documents that parse.
 */
export type ResolvedDispatchConfig = {
  enabled: boolean;
  defaultViewId: string;
  keymap: DispatchKeymap;
  showShortcuts: boolean;
  phoneTagSlug: string | null;
  /** Whether the task surface is on; workspace-only. */
  tasks: boolean;
  /** Whether a new task may be about a message or event; workspace-only, and read only while `tasks` is on. */
  itemTasks: boolean;
  views: Record<string, DispatchViewDefinition>;
  viewState: Record<string, DispatchViewState>;
};

/**
 * What is wrong with a `web.dispatch` block, as one `path: message` line each; empty when nothing is.
 *
 * For internal tooling, which refuses a document the schema does not admit; the web never asks.
 *
 * @param dispatch - The `web.dispatch` value of one stored or submitted layer; anything but an object passes.
 * @param schema - The strict block schema to hold it to; the workspace's unless told otherwise.
 */
export function dispatchConfigIssues(dispatch: unknown, schema: z.ZodType = DispatchConfigSchema): string[] {
  if (dispatch == null || typeof dispatch !== "object" || Array.isArray(dispatch)) {
    return [];
  }
  const result = schema.safeParse(dispatch);
  if (result.success) {
    return [];
  }
  return result.error.issues.map((issue) =>
    issue.path.length === 0 ? issue.message : `${issue.path.join(".")}: ${issue.message}`,
  );
}

/**
 * The code-default view: the launch layout minus anything tag-specific.
 *
 * Time, Name (pinned), Message; newest activity first; search over name and message; an "Unacknowledged only"
 * toggle on by default, so the table is the list of channels no dispatcher has handled yet. Unread is a row accent,
 * not a column; unacknowledged is the dot (carrying the count of waiting messages on a driver row), and a sort or
 * grouping, not a column either. A unit number is a workspace tag, so workspaces add it (and any other tag column or
 * filter) over this default through config. Never stored: the cascade holds only what someone wrote, and this view is
 * the layer beneath it ({@link withBuiltInDispatchViews}). Frozen, since every page hands it out by reference.
 */
export const DEFAULT_DISPATCH_VIEW: DispatchViewDefinition = deepFreeze({
  id: "default",
  label: "Default",
  query: {
    controls: [
      {
        kind: "toggle",
        id: "unacknowledged",
        label: "Unacknowledged only",
        field: "core:unacknowledged",
        default: true,
      },
    ],
    search: ["core:name", "core:recent_message"],
    sort: { field: "core:latest_item_at", direction: "desc" },
  },
  layout: {
    kind: "table",
    columns: [
      { field: "core:latest_item_at", label: "Time" },
      { field: "core:name", label: "Name", pinned: true },
      { field: "core:recent_message", label: "Message" },
    ],
  },
});

/**
 * The views a page offers: the built-in default beneath every view the cascade yields.
 *
 * The cascade stores only what someone wrote, so a workspace that never touched Dispatch yields no view at all; this
 * lays {@link DEFAULT_DISPATCH_VIEW} beneath as the layer under the cascade, in code rather than in config. A view
 * stored under `default` replaces it whole: the built-in is beneath the cascade, not a layer of it, so a stored view
 * that omits a sort is unsorted rather than inheriting the built-in's, where the same view stored over another layer's
 * would inherit that layer's ({@link explicitDispatchView} spells a saved view out so neither reading surprises). A
 * `null` entry, a reset that uncovered nothing, is dropped. The web and internal ops both read views through this, so
 * an operator customizing the default starts from what dispatchers see.
 *
 * @param views - The compiled `views` map, or none.
 */
export function withBuiltInDispatchViews(
  views: Record<string, DispatchViewDefinition | null | undefined> | null | undefined,
): Record<string, DispatchViewDefinition> {
  const offered: Record<string, DispatchViewDefinition> = { [DEFAULT_DISPATCH_VIEW.id]: DEFAULT_DISPATCH_VIEW };
  for (const [id, view] of Object.entries(views ?? {})) {
    if (view != null) {
      offered[id] = view;
    }
  }
  return offered;
}

/**
 * A view spelled out so that storing it over a view of the same id stores exactly it.
 *
 * The cascade lays a view over the stored one key by key, so a key the submitted view leaves out would inherit the
 * stored one's: a view switched back to the driver grain, or one pasted without a sort, would land as a hybrid of the
 * two. The three keys a definition may leave out are written explicitly instead: `grain` as `"driver"`, `sort` and
 * `groupBy` as `"none"`. Every path that stores a whole view writes this; the cascade's reading of the result is the
 * same as of the view submitted.
 */
export function explicitDispatchView(view: DispatchViewDefinition): DispatchViewDefinition {
  return {
    ...view,
    grain: view.grain ?? "driver",
    query: { ...view.query, sort: view.query.sort ?? NONE, groupBy: view.query.groupBy ?? NONE },
  };
}
